import React, { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Input } from '../components/ui/Input';
import { sectorsService, Sector, CreateSectorDto } from '../services/adminService';
import { Plus, Edit2, Trash2, X, Check, Loader2, Shield } from 'lucide-react';

export const SectorsPage: React.FC = () => {
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSector, setEditingSector] = useState<Sector | null>(null);
  const [formData, setFormData] = useState<CreateSectorDto>({
    name: '',
    description: '',
    regulator: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    loadSectors();
  }, []);

  const loadSectors = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await sectorsService.getAll();
      setSectors(response.sectors);
    } catch (err: any) {
      setError(err.message || 'Failed to load sectors');
    } finally {
      setIsLoading(false);
    }
  };

  const handleOpenModal = (sector?: Sector) => {
    if (sector) {
      setEditingSector(sector);
      setFormData({
        name: sector.name,
        description: sector.description || '',
        regulator: sector.regulator || '',
      });
    } else {
      setEditingSector(null);
      setFormData({ name: '', description: '', regulator: '' });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingSector(null);
    setFormData({ name: '', description: '', regulator: '' });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      if (editingSector) {
        await sectorsService.update(editingSector.id, formData);
      } else {
        await sectorsService.create(formData);
      }
      await loadSectors();
      handleCloseModal();
    } catch (err: any) {
      setError(err.message || 'Failed to save sector');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Are you sure you want to delete this sector?')) {
      return;
    }

    try {
      await sectorsService.delete(id);
      await loadSectors();
    } catch (err: any) {
      setError(err.message || 'Failed to delete sector');
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-secondary-900">Sectors</h1>
          <p className="text-secondary-600 mt-1">Manage care sectors</p>
        </div>
        <Button onClick={() => handleOpenModal()}>
          <Plus className="w-4 h-4 mr-2" />
          Add Sector
        </Button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-800">{error}</p>
        </div>
      )}

      {/* Sectors Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {sectors.map((sector) => (
          <Card key={sector.id} hover>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <CardTitle>{sector.name}</CardTitle>
                  {sector.regulator && (
                    <Badge variant="primary" size="sm" className="mt-2">
                      <Shield className="w-3 h-3 mr-1" />
                      {sector.regulator}
                    </Badge>
                  )}
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleOpenModal(sector)}
                    className="p-2 text-secondary-600 hover:text-primary-600 hover:bg-secondary-100 rounded-lg transition-colors"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDelete(sector.id)}
                    className="p-2 text-secondary-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-secondary-600">
                {sector.description || 'No description'}
              </p>
              <p className="text-xs text-secondary-500 mt-2">
                Created: {new Date(sector.createdAt).toLocaleDateString()}
              </p>
            </CardContent>
          </Card>
        ))}
      </div>

      {sectors.length === 0 && (
        <Card>
          <CardContent className="text-center py-12">
            <p className="text-secondary-600">No sectors found</p>
          </CardContent>
        </Card>
      )}

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {editingSector ? 'Edit Sector' : 'Create Sector'}
                </CardTitle>
                <button
                  onClick={handleCloseModal}
                  className="p-2 hover:bg-secondary-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5 text-secondary-600" />
                </button>
              </div>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <Input
                  label="Name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  placeholder="Sector name"
                />
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-1">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border border-secondary-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                    rows={3}
                    placeholder="Sector description"
                  />
                </div>
                <Input
                  label="Regulator"
                  value={formData.regulator}
                  onChange={(e) => setFormData({ ...formData, regulator: e.target.value })}
                  placeholder="e.g., Ofsted, CQC"
                />
                <div className="flex items-center justify-end space-x-3 pt-4">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleCloseModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        {editingSector ? 'Update' : 'Create'}
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

